/**
 * Phase 7.1 — Split group identity completion (computed-first).
 *
 * Doctrine:
 *   - Bucket 4 §4.x   Ticket splitting + manual merge + cross-split sync.
 *                     A split group must preserve stable conceptual
 *                     identity across audit, cascade, Due Out, and
 *                     completion lifecycles.
 *   - Bucket 4 §4.x   Splits may have 2, 3, or more parts. Binary is the
 *                     common case but not the rule.
 *   - Bucket 4 §4.x   Rollover / continuation tickets are NOT new
 *                     job-line splits — they are continuations of an
 *                     existing part across an EOD boundary.
 *
 * Why this module exists
 * ----------------------
 * Schema introduced in migration `20251103125606` added three columns:
 *
 *   - `tickets.split_parent_id` (text)   — original ticket ID before split
 *   - `tickets.split_group_id`  (text)   — shared UUID across all siblings
 *   - `tickets.split_sequence`  (integer)— 1-based sequence number
 *
 * The Compliance Master Fix List flagged identity as incomplete because:
 *   - There is NO `split_part_total` column (zero references repo-wide).
 *   - `SplitLink.tsx:20` hardcodes "Part N of 2", assuming binary.
 *   - `mergeSplitTickets` in `ticketMerging.ts` queries by group_id but
 *     does no integrity validation (gaps, duplicates, parent mismatch).
 *   - EOD rollover Part B tickets in `execute-manual-split` and
 *     `eodRolloverHandler` set BOTH `split_group_id` AND
 *     `rollover_of_ticket_id`, which can be mistaken for a job-line
 *     sibling when it is actually a continuation.
 *   - No formal vocabulary for warning conditions (orphan part,
 *     duplicate sequence, non-contiguous, etc.).
 *
 * Phase 7.1 supplies the canonical reconstruction vocabulary + pure
 * helpers so Phase 7.2 ("Part X of Y" UI) can consume identity from a
 * single source of truth.
 *
 * Phase 7.1 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     in-memory ticket projections.
 *   - **No DB migration**. The decision is computed-first — sibling
 *     count is derived from the group's ticket set, not stored. A
 *     future `split_part_total` column can be adopted via
 *     `SplitGroupReconstructionMode = 'schema_split_part_total'` if
 *     persisted invariants justify it later.
 *   - No runtime wiring. Existing `execute-manual-split`, `split-ticket`,
 *     `ticketMerging.findSplitSiblings`, `SplitLink.tsx`, and
 *     `dueOutSplitMergeValidation.ts` are UNTOUCHED.
 *
 * Builds on:
 *   - Phase 5.5 `dueOutSplitMergeValidation` (split/merge Due Out gate).
 *   - Phase 1.1 `getProductionState` reference shape (this helper does
 *     NOT pull state — it operates on identity columns only).
 */

import { isTicketDeleted } from '@/utils/ticketDeletedState';

// =============================================================================
// Vocabulary
// =============================================================================

/**
 * How the part-total was derived for a reconstructed group.
 *
 *   - 'computed'                  — derived from sibling count.
 *   - 'schema_split_part_total'   — read from a hypothetical persisted
 *                                   `split_part_total` column. Not present
 *                                   today; included so adoption can swap.
 *   - 'mixed'                     — both computed AND schema present and
 *                                   in agreement.
 */
export type SplitGroupReconstructionMode =
  | 'computed'
  | 'schema_split_part_total'
  | 'mixed';

export const SPLIT_GROUP_RECONSTRUCTION_MODES = Object.freeze([
  'computed',
  'schema_split_part_total',
  'mixed',
] as const) satisfies ReadonlyArray<SplitGroupReconstructionMode>;

/**
 * Discrete warning codes the reconstruction emits when split metadata
 * is missing or internally inconsistent. The helper never throws;
 * warnings are returned alongside the best-effort identity.
 */
export type SplitGroupWarning =
  | 'missing_split_group_id'
  | 'missing_split_parent_id'
  | 'missing_split_sequence'
  | 'duplicate_split_sequence'
  | 'non_contiguous_split_sequence'
  | 'inconsistent_parent_id'
  | 'inconsistent_group_id'
  | 'missing_part_total'
  | 'computed_total_disagrees_with_schema_total'
  | 'rollover_continuation_not_job_line_split'
  | 'orphan_split_part'
  | 'no_siblings_found'
  | 'missing_job_line_mapping'
  | 'missing_assignment_context';

export const SPLIT_GROUP_WARNINGS = Object.freeze([
  'missing_split_group_id',
  'missing_split_parent_id',
  'missing_split_sequence',
  'duplicate_split_sequence',
  'non_contiguous_split_sequence',
  'inconsistent_parent_id',
  'inconsistent_group_id',
  'missing_part_total',
  'computed_total_disagrees_with_schema_total',
  'rollover_continuation_not_job_line_split',
  'orphan_split_part',
  'no_siblings_found',
  'missing_job_line_mapping',
  'missing_assignment_context',
] as const) satisfies ReadonlyArray<SplitGroupWarning>;

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Permissive ticket shape. Every field optional so callers can pass
 * partial projections from any of the production tables. The helper
 * reads only what is named here.
 */
export interface SplitGroupTicketShape {
  id?: string | null;
  /** Original ticket ID before the split (legacy: snake_case). */
  split_parent_id?: string | null;
  /** Shared UUID across all siblings in the same split group. */
  split_group_id?: string | null;
  /** 1-based sequence number within the group. */
  split_sequence?: number | null;
  /**
   * Optional future schema column. Not present today; included so the
   * helper can compare a persisted total against the computed total.
   */
  split_part_total?: number | null;
  /**
   * Rollover lineage. When set, the ticket is a continuation of
   * `rollover_of_ticket_id`, NOT a new job-line sibling. Job-line
   * split children have this null.
   */
  rollover_of_ticket_id?: string | null;
  /** Rollover parent (matches `parent_ticket_id` in canonical schema). */
  parent_ticket_id?: string | null;
  /** Reason the row was created (e.g., 'manual_split', 'EOD_ROLLOVER'). */
  split_reason?: string | null;
  /** Tech assignment. */
  assigned_tech_id?: string | null;
  /** Bay assignment. */
  bay_id?: string | null;
  /** Lane assignment (specialty-bay lane id, when applicable). */
  lane_id?: string | null;
  /** Status (used for soft-tombstone exclusion). */
  status?: string | null;
  /** Scheduled date (informational, not used in identity math). */
  scheduled_date?: string | null;
}

/**
 * Optional per-ticket job-line mapping for callers that want job lines
 * carried into the reconstruction. The helper does not load jobs
 * itself — it accepts what the caller has already fetched.
 */
export interface SplitJobLineMapping {
  ticketId: string;
  jobIds: string[];
}

export interface ReconstructOptions {
  /** Optional job-line mappings to merge into part identities. */
  jobLineMappings?: ReadonlyArray<SplitJobLineMapping> | null;
}

// =============================================================================
// Output shapes
// =============================================================================

/**
 * Identity of a single part within a split group. `partIndex` and
 * `partTotal` are 1-based; `null` when the underlying data is missing.
 */
export interface SplitPartIdentity {
  ticketId: string;
  splitGroupId: string | null;
  splitParentId: string | null;
  splitSequence: number | null;
  partIndex: number | null;
  partTotal: number | null;
  /** True when this row is a job-line split child (no rollover lineage). */
  isJobLineSplit: boolean;
  /** True when this row is a rollover / EOD-continuation, not a new sibling. */
  isRolloverContinuation: boolean;
  assignedTechId: string | null;
  bayId: string | null;
  laneId: string | null;
  /** Job IDs assigned to this part (from caller-supplied mappings). */
  jobLineIds: string[];
  /** Per-part warnings — subset of group warnings filtered to this part. */
  warnings: SplitGroupWarning[];
}

/**
 * Identity of a reconstructed split group. `parts` excludes rollover
 * continuations; continuations are surfaced via `continuations` so
 * callers can render them separately.
 */
export interface SplitGroupIdentity {
  splitGroupId: string;
  /** Parent ticket id (when at least one child references it). */
  splitParentId: string | null;
  /** Total number of distinct job-line parts in the group. */
  partTotal: number;
  /** How `partTotal` was derived (computed / schema / mixed). */
  partTotalSource: SplitGroupReconstructionMode;
  /** Job-line split parts (excludes rollover continuations). */
  parts: SplitPartIdentity[];
  /** Rollover / continuation tickets that share `split_group_id`. */
  continuations: SplitPartIdentity[];
  /** All ticket rows the helper considered (parts + continuations). */
  siblings: ReadonlyArray<SplitGroupTicketShape>;
  /** Group-level warnings. */
  warnings: SplitGroupWarning[];
}

/**
 * Output of `reconstructSplitGroup`. Tickets are bucketed by
 * `split_group_id`; rows with no `split_group_id` end up in `ungrouped`.
 */
export interface SplitGroupReconstruction {
  groups: SplitGroupIdentity[];
  ungrouped: SplitGroupTicketShape[];
  warnings: SplitGroupWarning[];
}

/**
 * Convenience output of `getSplitPartLabelData` — the minimum data
 * Phase 7.2's UI label needs to render "Part X of Y".
 */
export interface SplitPartLabelData {
  partIndex: number | null;
  partTotal: number | null;
  /** Pre-formatted label or null when identity is incomplete. */
  label: string | null;
}

// =============================================================================
// Internal helpers
// =============================================================================

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

function isJobLineChild(t: SplitGroupTicketShape): boolean {
  return (
    !!t.split_group_id &&
    t.rollover_of_ticket_id == null &&
    t.split_parent_id != null
  );
}

function isContinuation(t: SplitGroupTicketShape): boolean {
  return !!t.rollover_of_ticket_id;
}

function isParentRow(t: SplitGroupTicketShape): boolean {
  // The original parent before split: shares the split_group_id with
  // its children (per execute-manual-split / split-ticket creation
  // logic) but is NOT itself a child of a split.
  return !!t.split_group_id && t.split_parent_id == null && !isContinuation(t);
}

function getJobsForTicket(
  ticketId: string,
  mappings: ReadonlyArray<SplitJobLineMapping> | null | undefined,
): string[] {
  if (!mappings) return [];
  const m = mappings.find(x => x.ticketId === ticketId);
  return m ? [...m.jobIds] : [];
}

// =============================================================================
// Public predicates
// =============================================================================

/**
 * Is this ticket part of a split group? True when `split_group_id` is
 * set OR when `split_parent_id` is set (the row is a split child).
 */
export function isTicketPartOfSplitGroup(t: SplitGroupTicketShape): boolean {
  return !!t.split_group_id || !!t.split_parent_id;
}

/**
 * Is this ticket a rollover/continuation rather than a job-line split?
 *
 * The distinguishing field is `rollover_of_ticket_id`. EOD rollover
 * Part B tickets set BOTH `split_group_id` AND `rollover_of_ticket_id`,
 * so a `split_group_id` check alone is NOT sufficient. Per
 * `execute-manual-split/index.ts:665-670` and `eodRolloverHandler`.
 */
export function isRolloverContinuationNotJobLineSplit(
  t: SplitGroupTicketShape,
): boolean {
  return isContinuation(t);
}

// =============================================================================
// Sibling enumeration
// =============================================================================

/**
 * Return all tickets that share the same `split_group_id` as `t`.
 * Excludes soft-deleted rows (`status === 'Deleted'`). Caller controls
 * the input set; the helper does not query the DB.
 *
 * Returns an empty array when `t` has no `split_group_id`.
 */
export function getSplitSiblingTickets(
  t: SplitGroupTicketShape,
  tickets: ReadonlyArray<SplitGroupTicketShape>,
): SplitGroupTicketShape[] {
  if (!t.split_group_id) return [];
  const out: SplitGroupTicketShape[] = [];
  for (const other of tickets) {
    if (other.split_group_id !== t.split_group_id) continue;
    if (isTicketDeleted({ status: other.status })) continue;
    out.push(other);
  }
  return out;
}

// =============================================================================
// Part total computation
// =============================================================================

/**
 * Compute the job-line part total for a group given its sibling set.
 *
 * Definition (Phase 7.1):
 *   partTotal = count of DISTINCT ticket ids in `siblings` that are
 *   job-line split children (`split_parent_id != null` AND
 *   `rollover_of_ticket_id == null`).
 *
 * Rollover continuations are EXCLUDED — they are not new parts, they
 * are continuations of an existing part. The original parent row
 * (if present, which only happens when the caller passes a pre-split
 * snapshot) is also excluded.
 */
export function getSplitPartTotal(
  siblings: ReadonlyArray<SplitGroupTicketShape>,
): number {
  const seen = new Set<string>();
  for (const s of siblings) {
    if (!isJobLineChild(s)) continue;
    if (!s.id) continue;
    seen.add(s.id);
  }
  return seen.size;
}

// =============================================================================
// Group warning detection
// =============================================================================

/**
 * Inspect a sibling set and emit identity warnings. Pure — does not
 * mutate inputs. The same warnings are surfaced inside
 * `getSplitGroupIdentity` and `reconstructSplitGroup`.
 */
export function getSplitGroupReconstructionWarnings(
  siblings: ReadonlyArray<SplitGroupTicketShape>,
): SplitGroupWarning[] {
  const warnings: SplitGroupWarning[] = [];
  if (siblings.length === 0) return warnings;

  // group_id consistency.
  const groupIds = new Set<string>();
  for (const s of siblings) {
    if (s.split_group_id) groupIds.add(s.split_group_id);
    else pushUnique(warnings, 'missing_split_group_id');
  }
  if (groupIds.size > 1) pushUnique(warnings, 'inconsistent_group_id');

  // parent_id consistency across job-line children only.
  const parentIds = new Set<string>();
  for (const s of siblings) {
    if (!isJobLineChild(s)) continue;
    if (s.split_parent_id) parentIds.add(s.split_parent_id);
    else pushUnique(warnings, 'missing_split_parent_id');
  }
  if (parentIds.size > 1) pushUnique(warnings, 'inconsistent_parent_id');

  // sequence checks — job-line children only.
  const sequences: number[] = [];
  const seenSequences = new Set<number>();
  for (const s of siblings) {
    if (!isJobLineChild(s)) continue;
    if (s.split_sequence == null) {
      pushUnique(warnings, 'missing_split_sequence');
      continue;
    }
    if (seenSequences.has(s.split_sequence)) {
      pushUnique(warnings, 'duplicate_split_sequence');
    } else {
      seenSequences.add(s.split_sequence);
      sequences.push(s.split_sequence);
    }
  }
  if (sequences.length >= 2) {
    sequences.sort((a, b) => a - b);
    // Expect 1..N contiguous. Non-1 start or gap → non_contiguous.
    if (sequences[0] !== 1) {
      pushUnique(warnings, 'non_contiguous_split_sequence');
    }
    for (let i = 1; i < sequences.length; i++) {
      if (sequences[i] !== sequences[i - 1] + 1) {
        pushUnique(warnings, 'non_contiguous_split_sequence');
        break;
      }
    }
  }

  // Rollover continuation present in the group.
  const hasContinuation = siblings.some(isContinuation);
  if (hasContinuation) pushUnique(warnings, 'rollover_continuation_not_job_line_split');

  // Orphan: only one job-line child found.
  const jobLineCount = siblings.filter(isJobLineChild).length;
  if (jobLineCount === 1) pushUnique(warnings, 'orphan_split_part');
  if (jobLineCount === 0) pushUnique(warnings, 'no_siblings_found');

  // Schema vs computed total disagreement.
  const computedTotal = getSplitPartTotal(siblings);
  const schemaTotals = new Set<number>();
  for (const s of siblings) {
    if (typeof s.split_part_total === 'number') schemaTotals.add(s.split_part_total);
  }
  if (schemaTotals.size > 0) {
    if (schemaTotals.size > 1) {
      pushUnique(warnings, 'computed_total_disagrees_with_schema_total');
    } else {
      const schemaTotal = [...schemaTotals][0];
      if (schemaTotal !== computedTotal) {
        pushUnique(warnings, 'computed_total_disagrees_with_schema_total');
      }
    }
  } else if (jobLineCount === 0) {
    pushUnique(warnings, 'missing_part_total');
  }

  return warnings;
}

// =============================================================================
// Group identity assembly
// =============================================================================

/**
 * Determine the `partTotalSource` from computed + schema availability.
 */
function resolvePartTotalSource(
  computedTotal: number,
  schemaTotal: number | null,
): SplitGroupReconstructionMode {
  if (schemaTotal == null) return 'computed';
  if (schemaTotal === computedTotal) return 'mixed';
  // Disagreement: prefer schema (caller-persisted) so the warning is
  // surfaced but the source reflects where the displayed total came
  // from. We use `'schema_split_part_total'` to signal that the helper
  // is reading the persisted value even though it disagrees with
  // computation; the warning communicates the mismatch.
  return 'schema_split_part_total';
}

/**
 * Build a single split group's identity from a ticket + its sibling
 * set. Returns `null` when the ticket is not part of any group.
 */
export function getSplitGroupIdentity(
  ticket: SplitGroupTicketShape,
  siblings: ReadonlyArray<SplitGroupTicketShape>,
  options?: ReconstructOptions | null,
): SplitGroupIdentity | null {
  const groupId = ticket.split_group_id;
  if (!groupId) return null;

  // Filter siblings to this group (defensive — caller may pass a mixed list).
  const groupSiblings = siblings.filter(s => s.split_group_id === groupId);
  if (groupSiblings.length === 0) return null;

  const warnings = getSplitGroupReconstructionWarnings(groupSiblings);
  const computedTotal = getSplitPartTotal(groupSiblings);

  // Resolve the parent id from the children's `split_parent_id`. When
  // multiple values are present, pick the first one (inconsistent_parent_id
  // warning already raised by getSplitGroupReconstructionWarnings).
  let resolvedParentId: string | null = null;
  for (const s of groupSiblings) {
    if (s.split_parent_id) {
      resolvedParentId = s.split_parent_id;
      break;
    }
  }

  // Resolve schema total (when present).
  let schemaTotal: number | null = null;
  for (const s of groupSiblings) {
    if (typeof s.split_part_total === 'number') {
      schemaTotal = s.split_part_total;
      break;
    }
  }

  const partTotal = schemaTotal != null ? schemaTotal : computedTotal;
  const partTotalSource = resolvePartTotalSource(computedTotal, schemaTotal);

  // Build per-part identities.
  const parts: SplitPartIdentity[] = [];
  const continuations: SplitPartIdentity[] = [];

  for (const s of groupSiblings) {
    if (!s.id) continue; // Skip rows missing an id.
    const isCont = isContinuation(s);
    const isJobLine = isJobLineChild(s);

    const partWarnings: SplitGroupWarning[] = [];
    if (!s.split_parent_id && isJobLine === false && !isParentRow(s)) {
      pushUnique(partWarnings, 'missing_split_parent_id');
    }
    if (s.split_sequence == null && isJobLine) {
      pushUnique(partWarnings, 'missing_split_sequence');
    }
    if (isCont) {
      pushUnique(partWarnings, 'rollover_continuation_not_job_line_split');
    }
    if (s.assigned_tech_id == null) {
      pushUnique(partWarnings, 'missing_assignment_context');
    }
    const jobLineIds = getJobsForTicket(s.id, options?.jobLineMappings);
    if (options?.jobLineMappings && jobLineIds.length === 0) {
      pushUnique(partWarnings, 'missing_job_line_mapping');
    }

    const identity: SplitPartIdentity = {
      ticketId: s.id,
      splitGroupId: groupId,
      splitParentId: s.split_parent_id ?? null,
      splitSequence: s.split_sequence ?? null,
      partIndex: isJobLine ? (s.split_sequence ?? null) : null,
      partTotal: isJobLine ? partTotal : null,
      isJobLineSplit: isJobLine,
      isRolloverContinuation: isCont,
      assignedTechId: s.assigned_tech_id ?? null,
      bayId: s.bay_id ?? null,
      laneId: s.lane_id ?? null,
      jobLineIds,
      warnings: partWarnings,
    };

    if (isCont) continuations.push(identity);
    else if (isJobLine) parts.push(identity);
    // Parent rows are intentionally not placed in either bucket — the
    // caller has them via `siblings`. Their identity is captured in
    // `splitParentId`.
  }

  // Sort parts by sequence (ascending), tie-broken by id for stability.
  parts.sort((a, b) => {
    const sa = a.splitSequence ?? Number.MAX_SAFE_INTEGER;
    const sb = b.splitSequence ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return a.ticketId.localeCompare(b.ticketId);
  });

  return {
    splitGroupId: groupId,
    splitParentId: resolvedParentId,
    partTotal,
    partTotalSource,
    parts,
    continuations,
    siblings: groupSiblings,
    warnings,
  };
}

/**
 * Convenience: build a single part's identity from its parent group.
 * Useful for UI components that already hold a `SplitGroupIdentity`
 * and need to render a specific ticket's row.
 */
export function getSplitPartIdentity(
  ticket: SplitGroupTicketShape,
  group: SplitGroupIdentity,
): SplitPartIdentity | null {
  if (!ticket.id) return null;
  const found =
    group.parts.find(p => p.ticketId === ticket.id) ??
    group.continuations.find(p => p.ticketId === ticket.id) ??
    null;
  return found;
}

// =============================================================================
// Full reconstruction (multi-group)
// =============================================================================

/**
 * Reconstruct ALL split groups present in a ticket set. Tickets with
 * no `split_group_id` are returned in `ungrouped` so callers can route
 * them through non-split codepaths. Aggregated warnings are surfaced
 * at the top level for cross-group diagnostics.
 *
 * Pure. Does not mutate inputs.
 */
export function reconstructSplitGroup(
  tickets: ReadonlyArray<SplitGroupTicketShape>,
  options?: ReconstructOptions | null,
): SplitGroupReconstruction {
  const groupsMap = new Map<string, SplitGroupTicketShape[]>();
  const ungrouped: SplitGroupTicketShape[] = [];

  for (const t of tickets) {
    if (t.split_group_id) {
      const arr = groupsMap.get(t.split_group_id) ?? [];
      arr.push(t);
      groupsMap.set(t.split_group_id, arr);
    } else {
      ungrouped.push(t);
    }
  }

  const groups: SplitGroupIdentity[] = [];
  const warnings: SplitGroupWarning[] = [];

  for (const [, siblings] of groupsMap) {
    // Pick any sibling for identity assembly — all share the group id.
    const head = siblings[0];
    const identity = getSplitGroupIdentity(head, siblings, options);
    if (!identity) continue;
    groups.push(identity);
    for (const w of identity.warnings) pushUnique(warnings, w);
  }

  // Sort groups by splitGroupId for stability.
  groups.sort((a, b) => a.splitGroupId.localeCompare(b.splitGroupId));

  return { groups, ungrouped, warnings };
}

// =============================================================================
// Label data for Phase 7.2 UI
// =============================================================================

/**
 * Build the "Part X of Y" label data for a single ticket within its
 * reconstructed group. Returns `null`-filled fields when the ticket
 * is not a job-line split (e.g., rollover continuation or
 * unidentifiable row).
 *
 * Phase 7.2's `SplitLink.tsx` rewrite should consume THIS helper
 * rather than format the label inline — making the binary hardcode
 * impossible to reintroduce.
 */
export function getSplitPartLabelData(
  ticket: SplitGroupTicketShape,
  group: SplitGroupIdentity,
): SplitPartLabelData {
  const identity = getSplitPartIdentity(ticket, group);
  if (!identity || !identity.isJobLineSplit) {
    return { partIndex: null, partTotal: null, label: null };
  }
  const idx = identity.partIndex;
  const total = identity.partTotal;
  if (idx == null || total == null || total < 1) {
    return { partIndex: idx, partTotal: total, label: null };
  }
  return { partIndex: idx, partTotal: total, label: `Part ${idx} of ${total}` };
}

// =============================================================================
// Phase 7.3 — board-level label data map
// =============================================================================

/**
 * Build a ticket-id keyed map of `SplitPartLabelData` from a tickets
 * array. Designed for one-time board-level computation: callers run
 * this ONCE per ticket-array change and thread the map down to each
 * ticket card (mirrors the `advisorBayHoldByTicketId` pattern in
 * `TechnicianColumn`).
 *
 * Behavior:
 *   - Every ticket with a `split_group_id` AND `id` gets an entry.
 *   - Rollover continuations and tickets that resolve to no usable
 *     `partIndex` / `partTotal` get a `{partIndex, partTotal,
 *     label: null}` entry. Consumers test `label != null` before
 *     rendering "Part X of Y"; a null entry means the consumer should
 *     fall through to the SplitLink "Split" fallback (or, for
 *     continuations, the existing `RolloverBadge`).
 *   - Tickets with no `split_group_id` are NOT inserted (caller can
 *     `map.get(id) ?? null`).
 *
 * Pure. Does not mutate inputs.
 */
export function buildSplitPartLabelDataMap(
  tickets: ReadonlyArray<SplitGroupTicketShape>,
): Map<string, SplitPartLabelData> {
  const out = new Map<string, SplitPartLabelData>();
  const reconstruction = reconstructSplitGroup(tickets);
  const groupsById = new Map<string, SplitGroupIdentity>();
  for (const g of reconstruction.groups) groupsById.set(g.splitGroupId, g);

  for (const t of tickets) {
    if (!t.id || !t.split_group_id) continue;
    const group = groupsById.get(t.split_group_id);
    if (!group) continue;
    out.set(t.id, getSplitPartLabelData(t, group));
  }
  return out;
}

/**
 * Convenience: read a single ticket's label data from a precomputed
 * map. Returns `null` when the ticket has no entry (e.g., not part of
 * any split group). Callers pass the result straight to
 * `<SplitLink splitLabelData={...} />`.
 */
export function getSplitLabelDataForTicket(
  ticketId: string | null | undefined,
  map: ReadonlyMap<string, SplitPartLabelData> | null | undefined,
): SplitPartLabelData | null {
  if (!ticketId || !map) return null;
  return map.get(ticketId) ?? null;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. SplitLink.tsx (Phase 7.2):
//    Replace the inline `Part ${splitSequence} of 2` (L20) with
//    `getSplitPartLabelData(ticket, group).label`. Caller computes
//    `group` once per board render via `reconstructSplitGroup(tickets)`.
//
// 2. ticketMerging.findSplitSiblings:
//    Optional adoption: after the existing DB SELECT returns siblings,
//    pass them through `reconstructSplitGroup([currentTicket,
//    ...siblings])` to get warnings before invoking the merge. Warnings
//    can be logged to ops audit without changing merge behavior.
//
// 3. Due Out validation (Phase 5.5):
//    `dueOutSplitMergeValidation.ts` already accepts a ticket array
//    for split validation. Wire `reconstructSplitGroup` to detect
//    orphan / non-contiguous / inconsistent-parent groups BEFORE
//    running the Due Out projection. No projection logic change.
//
// 4. Schema migration (deferred):
//    Adding `tickets.split_part_total` is NOT necessary today —
//    computed reconstruction handles 2+ parts correctly. If a future
//    invariant requires persistence (e.g., disconnected clients
//    that can't load siblings), the helper accepts `split_part_total`
//    via `SplitGroupTicketShape.split_part_total` and emits
//    `computed_total_disagrees_with_schema_total` when they differ.
//
// 5. Rollover continuation tagging:
//    Adoption-wise, callers can check
//    `isRolloverContinuationNotJobLineSplit(ticket)` before applying
//    sibling-merge semantics. This prevents merging an EOD Part B
//    continuation as if it were a job-line sibling.
//
// 6. Audit:
//    Reserve `SPLIT_GROUP_RECONSTRUCTED`, `SPLIT_GROUP_WARNING_RAISED`
//    in `AdminActionType` when persistence begins. The reconstruction
//    output is reproducible per-call so callers can log warnings on
//    detection without storing the verdict.
